import React, { useState, useEffect, useContext } from 'react';
import { getRedirectResult } from "firebase/auth";
import { auth } from './firebase.js';
import { useDispatch } from 'react-redux';
import { login } from './userSlice';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { useTheme } from './ThemeContext';
import Spinner from './Spinner.js';
import bgImage from './images/truck_blue_snow.jpg';
import ContactForm from './ContactUsForm.js';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from "@fortawesome/free-solid-svg-icons";
import queryString from "query-string";
import ReactPixel from "react-facebook-pixel";
import { useLocation } from "react-router-dom";
import { processAffiliateParams, getAffiliateData } from './affiliateHelpers';

function LoginDemo() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentUser, loginWithEmail } = useContext(AuthContext);
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);

  const { carrierPortal } = theme;
  const location = useLocation();
  
  const getNavLinkClass = (path) => {
    const isActive = location.pathname === path || (path === '/' && location.pathname === '/logindemo');
    return isActive 
      ? "text-orange-500 hover:text-orange-400 transition font-semibold"
      : "text-gray-200 hover:text-gray-300 transition";
  };

  const handleContactClick = (e) => {
    e.preventDefault();
    
    // If we're already on the home page, just scroll to contact
    if (location.pathname === '/' || location.pathname === '/logindemo') {
      const contactElement = document.getElementById('contact');
      if (contactElement) {
        contactElement.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home page first, then scroll to contact
      navigate('/#contact');
    }
    
    // Close mobile nav if open
    setIsNavOpen(false);
  };

  useEffect(() => {
      const params = queryString.parse(window.location.search);
      if (Object.keys(params).length > 0) {
        localStorage.setItem("marketingParams", JSON.stringify(params));
      }
      
      // Process affiliate parameters
      const searchParams = new URLSearchParams(window.location.search);
      const affiliateData = processAffiliateParams(searchParams);
      
      if (affiliateData) {
        console.log('Affiliate tracking activated:', affiliateData);
        // Optional: Send affiliate tracking event to analytics
        ReactPixel.track('AffiliateVisit', {
          refCode: affiliateData.refCode,
          source: affiliateData.source
        });
      }
    }, []);

    useEffect(() => {
      const pageName = location.pathname.replace("/", "") || "Home";
  
      ReactPixel.pageView({
        page: pageName, 
        path: location.pathname,
        url: window.location.href
      });  

    }, [location]);

    // Handle scrolling to contact section when URL has #contact hash
    useEffect(() => {
      if (location.hash === '#contact') {
        // Use a timeout to ensure the page has rendered
        const timer = setTimeout(() => {
          const contactElement = document.getElementById('contact');
          if (contactElement) {
            contactElement.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);

        return () => clearTimeout(timer);
      }
    }, [location.hash]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const loginTimeout = setTimeout(() => {
      setIsLoading(false);
      setError('Login process is taking too long. Please try again.');
    }, 15000);

    try {
      await loginWithEmail(email, password);
      clearTimeout(loginTimeout);
    } catch (err) {
      console.error("Error Logging In:", err);
      setError(err.message);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      // Navigate based on user type
      if (currentUser.isAffiliate === 'True' || currentUser.isAffiliate === true || currentUser.Roles === 'Affiliate') {
        navigate('/affiliate-dashboard');
      } else {
        navigate('/dashboard');
      }
    } else {
      getRedirectResult(auth)
        .then((result) => {
          if (result && result.user) {
            const { displayName, email, uid, photoURL } = result.user;
            dispatch(login({ displayName, email, uid, photoURL }));
            // Navigation will be handled by the updated currentUser in the next render
          }
        })
        .catch((error) => {
          console.error("Error in getRedirectResult:", error);
          setError(error.message);
        });
    }
  }, [currentUser, dispatch, navigate]);

  return (
    <>
      {isLoading && <Spinner />}

      <div className="min-h-screen flex flex-col">
        {/* Header */}
        <header className="top-0 z-50 bg-gray-900 shadow-md">
          <div className="container mx-auto px-6 py-2 flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <img
                src="/logos/fln_logo_white.png"
                alt="FuelLink Logo"
                className="h-20 w-auto object-contain"
              />
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex space-x-6">
              <Link to="/" className={getNavLinkClass('/')}>
                Home
              </Link>
              <Link to="/locationfinderdemo" className={getNavLinkClass('/locationfinderdemo')}>
                Network
              </Link>
              <a href="#contact" onClick={handleContactClick} className="text-gray-200 hover:text-gray-300 transition">
                Contact
              </a>
              <Link to="/affiliate-signup" className={getNavLinkClass('/affiliate-signup')}>
                Partners
              </Link>
              <Link to="/registration" className={getNavLinkClass('/registration')}>
                Sign Up
              </Link>
            </nav>

            {/* Hamburger for mobile */}
            <div className="md:hidden">
              <button
                onClick={() => setIsNavOpen(!isNavOpen)}
                className="text-gray-200 focus:outline-none"
              >
                <FontAwesomeIcon icon={faBars} className="text-orange-500 text-4xl" />
              </button>
            </div>
          </div>

          {/* Mobile Nav (only visible when isNavOpen = true) */}
          <nav
            className={`
      md:hidden transition-all duration-300 bg-gray-900
      ${isNavOpen ? 'block' : 'hidden'}
    `}
          >
            <div className="px-6 py-2">
              <Link
                to="/"
                className={`block hover:bg-gray-700 rounded py-2 ${getNavLinkClass('/').includes('orange') ? 'text-orange-500 font-semibold' : 'text-gray-200'}`}
                onClick={() => setIsNavOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/locationfinderdemo"
                className={`block hover:bg-gray-700 rounded py-2 ${getNavLinkClass('/locationfinderdemo').includes('orange') ? 'text-orange-500 font-semibold' : 'text-gray-200'}`}
                onClick={() => setIsNavOpen(false)}
              >
                Network
              </Link>
              <a
                href="#contact"
                className="block text-gray-200 hover:bg-gray-700 rounded py-2"
                onClick={handleContactClick}
              >
                Contact
              </a>
              <Link
                to="/affiliate-signup"
                className={`block hover:bg-gray-700 rounded py-2 ${getNavLinkClass('/affiliate-signup').includes('orange') ? 'text-orange-500 font-semibold' : 'text-gray-200'}`}
                onClick={() => setIsNavOpen(false)}
              >
                Partners
              </Link>
              <Link
                to="/registration"
                className={`block hover:bg-gray-700 rounded py-2 ${getNavLinkClass('/registration').includes('orange') ? 'text-orange-500 font-semibold' : 'text-gray-200'}`}
                onClick={() => setIsNavOpen(false)}
              >
                Sign Up
              </Link>
            </div>
          </nav>
        </header>


        {/* Hero Section */}
        <div
          className="
          bg-no-repeat 
          bg-cover 
          bg-center      /* Ensures the focal point stays centered on smaller screens */
          md:bg-fixed    /* Only apply 'fixed' on medium screens and above */
          text-gray-100
        "
          style={{
            backgroundImage: `url(${bgImage})`,
            backgroundAttachment: 'fixed'
          }}
        >
          <div className="mx-auto px-6 md:px-24 py-10 flex flex-col md:flex-row items-center bg-black/40">
            <div className="w-full md:w-2/3">
              <h1 className="text-5xl font-bold mb-4">
                More Discounts. More Control. More Miles.
              </h1>
              <p className="text-lg md:text-lg mb-6">
                Take your fleet further with nationwide fuel savings at over 4500 locations,
                full card controls, and the power of the EFS network.
              </p>
            </div>
            <div className="w-full md:w-1/4 mt-8 md:mt-0 bg-gray-800/90 p-6 rounded-lg shadow">
              <h2 className="text-xl mb-4 text-center text-gray-100">Login</h2>
              {error && <p className="text-red-500 mb-4">{error}</p>}

              <form onSubmit={handleLogin}>
                <div className="mb-4">
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full p-2 border border-gray-600 rounded bg-gray-700 text-gray-100"
                  />
                </div>
                <div className="mb-4">
                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full p-2 border border-gray-600 rounded bg-gray-700 text-gray-100"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full themed-login-button p-2 rounded mb-4"
                >
                  Login
                </button>
              </form>

              <div className="text-center text-gray-100">
                {!carrierPortal && (
                  <div>
                    <Link to="/registration" className="text-blue-400 hover:underline">
                      Sign Up
                    </Link>
                  </div>
                )}
                <div>
                  <Link to="/resetpassword" className="text-blue-400 hover:underline">
                    Forgot Password?
                  </Link>
                </div>
                {!carrierPortal && (
                  <div>
                    <Link to="/faq" className="text-blue-400 hover:underline">
                      FAQ's
                    </Link>
                  </div>
                )}
                {carrierPortal && (
                  <Link to="/signup" className="text-blue-400 hover:underline">
                    Redeem an Invitation Code
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-b from-gray-900 via-gray-700 to-gray-900 text-gray-100 py-16">
          <div className="mx-auto px-6">
            {/* <h2 className="text-4xl font-bold mb-8 text-center">Why Choose FLN?</h2> */}

            <div className="space-y-8">
              <div className="flex flex-col bg-gray-800/50 rounded-lg shadow-md p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-white">A Powerful Solution for Owner Operators & Fleets</h3>
                  <p className="text-gray-300 mt-2">
                    Whether you're an independent owner-operator or managing a fleet, we provide solutions tailored to your needs.
                  </p>
                </div>
                <div className="text-center text-gray-300 mb-6 space-y-4">
                  <p>
                    <strong>For Owner Operators:</strong> Access exclusive fleet discounts that help you save on fuel and operational costs.
                  </p>
                  <p>
                    <strong>For Fleets:</strong> Take advantage of industry-leading fuel discounts at over 4,500 locations nationwide.
                    If you're looking for a more customized solution, we offer API integrations, profit-sharing programs,
                    and a fully integrated TMS to streamline operations and maximize efficiency.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <img
                    src="/images/screenshot_fueltrans_user.png"
                    alt="Fuel Transactions"
                    className="w-full h-48 object-contain rounded-lg"
                  />
                  <img
                    src="/images/FLN_CARD_DISPLAY.png"
                    alt="FLN Fuel Card"
                    className="w-full h-48 object-contain rounded-lg"
                  />
                  <img
                    src="/images/screenshot_locationfinder.png"
                    alt="Profit Sharing Model"
                    className="w-full h-48 object-contain rounded-lg"
                  />
                </div>
              </div>

              <div className="flex flex-col bg-gray-800/50 rounded-lg shadow-md p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-white">Extensive Network</h3>
                  <p className="text-gray-300 mt-2">
                    Access over 4,500+ fuel locations nationwide, more than any other network.
                  </p>
                  <div className="text-center mt-4">
                  <Link
                    to="/locationfinderdemo"
                    className="inline-block px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-md shadow-lg"
                  >
                    Explore Our Discounts
                  </Link>
                </div>
                  <p className="mt-5">Some of our partners include:</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
                  <div className="col-span-1 md:col-span-3 flex justify-center my-4">
                    <img
                      src="/logos/ta-tri-logo-primary.svg"
                      alt="Combined Fuel Locations"
                      className="w-3/4 h-32 object-contain"
                    />
                  </div>
                  <div className="col-span-1 md:col-span-3 flex justify-center my-4 flex-wrap">
                    <img
                      src="/logos/speedwayLogo.svg"
                      alt="Fuel Location 1"
                      className="w-24 h-24 object-contain mx-2"
                    />
                    <img
                      src="/logos/racetracLogo.png"
                      alt="Fuel Location 2"
                      className="w-24 h-24 object-contain mx-2"
                    />
                    <img
                      src="/logos/roadRangerLogo.png"
                      alt="Fuel Location 3"
                      className="w-24 h-24 object-contain mx-2"
                    />
                    <img
                      src="/logos/quarlesLogo.svg"
                      alt="Fuel Location 5"
                      className="w-24 h-24 object-contain mx-2"
                    />
                    <img
                      src="/logos/caseysLogo.svg"
                      alt="Fuel Location 4"
                      className="w-24 h-24 object-contain mx-2"
                    />
                  </div>
                  <div className="col-span-1 md:col-span-3 flex justify-center my-4 space-x-4 flex-wrap">
                    <img
                      src="/logos/sappBrosLogo.png"
                      alt="Fuel Location 4"
                      className="w-32 h-28 object-contain"
                    />
                    <img
                      src="/logos/maverikLogo.svg"
                      alt="Fuel Location 5"
                      className="w-32 h-28 object-contain"
                    />
                    <img
                      src="/logos/ambest-logo.png"
                      alt="Fuel Location 6"
                      className="w-32 h-28 object-contain"
                    />
                  </div>
                </div>
              </div>             
              <div className="flex flex-col bg-gray-800/50 rounded-lg shadow-md p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-white">White Label Solutions</h3>
                  <p className="text-gray-300 mt-2">
                    Create a customized fuel program for fleets of 5 or more with our out-of-the-box solutions.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <img
                    src="/logos/Display_blue_theme.png"
                    alt="Partner 1"
                    className="w-full h-60 object-contain mx-auto"
                  />
                  <img
                    src="/logos/Display_green_theme.png"
                    alt="Partner 2"
                    className="w-full h-60 object-contain mx-auto"
                  />
                  <img
                    src="/logos/Display_red_theme.png"
                    alt="Partner 3"
                    className="w-full h-60 object-contain mx-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div id="contact" className="bg-gray-800 text-gray-100 py-16">
          <ContactForm />
        </div>

        <footer className="bg-black text-gray-300 py-6">
          <div className="container mx-auto px-6 text-center">
            &copy; {new Date().getFullYear()} FLN Fuel Network. All rights reserved.
          </div>
        </footer>
      </div>
    </>
  );
}

export default LoginDemo;